/*
  TSS Collection List — horizontal scroller behaviour (arrows + desktop drag-to-scroll).
  Shared by the "TSS Collection List" section and the collection page sub-collection tiles.
*/
(() => {
  class TssCollectionList extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      const track = this.querySelector('.tss-collection-list__track');
      if (!track) return;

      const prevBtn = this.querySelector('.tss-collection-list__arrow--prev');
      const nextBtn = this.querySelector('.tss-collection-list__arrow--next');

      const updateArrows = () => {
        if (!prevBtn && !nextBtn) return;
        const canScroll = track.scrollWidth > track.clientWidth + 1;
        const atStart = track.scrollLeft <= 1;
        const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
        if (prevBtn) prevBtn.hidden = !canScroll || atStart;
        if (nextBtn) nextBtn.hidden = !canScroll || atEnd;
      };

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const scrollByAmount = (dir) => {
        track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: reduceMotion ? 'auto' : 'smooth' });
      };

      if (prevBtn) prevBtn.addEventListener('click', () => scrollByAmount(-1), { signal });
      if (nextBtn) nextBtn.addEventListener('click', () => scrollByAmount(1), { signal });

      track.addEventListener('scroll', updateArrows, { passive: true, signal });
      window.addEventListener('resize', updateArrows, { signal });

      if (window.ResizeObserver) {
        /* updateArrows toggles [hidden] on the arrows, which is a layout write.
           Doing that inside the observer callback trips the browser's
           "ResizeObserver loop completed with undelivered notifications" error,
           so defer it a frame. */
        let rafId = null;
        this.resizeObserver = new ResizeObserver(() => {
          if (rafId !== null) return;
          rafId = window.requestAnimationFrame(() => {
            rafId = null;
            updateArrows();
          });
        });
        this.resizeObserver.observe(track);
      }

      updateArrows();

      if (this.dataset.dragScroll === 'true') {
        this.initDragScroll(track, signal);
      }
    }

    initDragScroll(track, signal) {
      const DRAG_THRESHOLD = 5;
      let isPointerDown = false;
      let isDragging = false;
      let justDragged = false;
      let startX = 0;
      let startScroll = 0;
      let activePointerId = null;

      track.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType === 'touch') return;
          if (event.button !== 0) return;
          if (!window.matchMedia('(min-width: 769px)').matches) return;
          isPointerDown = true;
          isDragging = false;
          startX = event.clientX;
          startScroll = track.scrollLeft;
          activePointerId = event.pointerId;
        },
        { signal }
      );

      track.addEventListener(
        'pointermove',
        (event) => {
          if (!isPointerDown) return;
          const dx = event.clientX - startX;

          if (!isDragging) {
            if (Math.abs(dx) < DRAG_THRESHOLD) return;
            isDragging = true;
            track.classList.add('is-dragging');
            try {
              track.setPointerCapture(activePointerId);
            } catch (e) {}
          }

          event.preventDefault();
          track.scrollLeft = startScroll - dx;
        },
        { signal }
      );

      const endPointer = () => {
        if (!isPointerDown) return;
        isPointerDown = false;
        if (activePointerId !== null) {
          try {
            track.releasePointerCapture(activePointerId);
          } catch (e) {}
        }
        activePointerId = null;
        if (isDragging) {
          isDragging = false;
          track.classList.remove('is-dragging');
          justDragged = true;
          setTimeout(() => {
            justDragged = false;
          }, 0);
        }
      };

      track.addEventListener('pointerup', endPointer, { signal });
      track.addEventListener('pointercancel', endPointer, { signal });

      track.addEventListener(
        'click',
        (event) => {
          if (justDragged) {
            event.preventDefault();
            event.stopPropagation();
            justDragged = false;
          }
        },
        { capture: true, signal }
      );
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
    }
  }

  if (!customElements.get('tss-collection-list')) {
    customElements.define('tss-collection-list', TssCollectionList);
  }
})();
